import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead, { seoConfigs } from '@/components/SEOHead';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { ChevronRight, Home } from 'lucide-react';

const SITE_URL = 'https://www.practice4sat.com';

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  'name': 'Contact Us | Practice4SAT',
  'description': 'Get in touch with the Practice4SAT team. We are here to help with questions about practice tests, your account, or technical support.',
  'url': `${SITE_URL}/contact`,
  'inLanguage': 'en-US',
  'isPartOf': {
    '@type': 'WebSite',
    'name': 'Practice4SAT',
    'url': SITE_URL
  },
  'publisher': {
    '@type': 'Organization',
    'name': 'Practice4SAT',
    'url': SITE_URL,
    'contactPoint': {
      '@type': 'ContactPoint',
      'contactType': 'customer service',
      'email': 'support@practice4sat.com',
      'url': `${SITE_URL}/contact`
    }
  },
  'breadcrumb': {
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': SITE_URL },
      { '@type': 'ListItem', 'position': 2, 'name': 'Contact Us', 'item': `${SITE_URL}/contact` }
    ]
  }
};

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [responseMessage, setResponseMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setResponseMessage('All fields are required.');
      return;
    }

    setIsSubmitting(true);
    setResponseMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setResponseMessage('Thank you for your message! We will get back to you soon.');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setResponseMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setResponseMessage('Failed to send message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column' }}>
      <SEOHead {...seoConfigs.contact} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />

      <Header variant="public" />

      <main style={{ flex: 1 }}>
        {/* Breadcrumb navigation */}
        <nav aria-label="Breadcrumb" data-testid="breadcrumb-nav" style={{
          padding: '1rem 2rem 0',
          maxWidth: '1400px',
          margin: '0 auto',
        }}>
          <ol style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            listStyle: 'none',
            padding: 0,
            margin: 0,
            fontSize: '0.9rem',
            fontFamily: "'Arial', sans-serif",
          }}>
            <li>
              <Link to="/" style={{ color: '#6b7280', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <Home size={14} aria-hidden="true" />
                <span>Home</span>
              </Link>
            </li>
            <li aria-hidden="true" style={{ color: '#9ca3af' }}>
              <ChevronRight size={14} />
            </li>
            <li>
              <span aria-current="page" style={{ color: '#0030b3', fontWeight: '600' }}>
                Contact Us
              </span>
            </li>
          </ol>
        </nav>

        <article style={{ padding: '2rem 2rem', maxWidth: '1400px', margin: '0 auto' }}>
          <h1 data-testid="contact-title" style={{ fontSize: '1.5rem', fontWeight: '600', textAlign: 'center', marginTop: 0, marginBottom: '0.5rem', color: '#0030b3' }}>
            Contact Us
          </h1>
          <p style={{ textAlign: 'center', maxWidth: '720px', margin: '0 auto 2rem', color: '#666', fontFamily: "'Arial', sans-serif" }}>
            Have questions or feedback? We'd love to hear from you!
          </p>

          <section aria-labelledby="contact-form-heading" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 id="contact-form-heading" className="sr-only" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', border: 0 }}>
              Send Us a Message
            </h2>

            <form onSubmit={handleSubmit} data-testid="contact-form" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <label htmlFor="name" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333', fontFamily: "'Arial', sans-serif" }}>Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  data-testid="contact-name-input"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '0.375rem',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333', fontFamily: "'Arial', sans-serif" }}>Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  data-testid="contact-email-input"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '0.375rem',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label htmlFor="message" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333', fontFamily: "'Arial', sans-serif" }}>Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="6"
                  data-testid="contact-message-input"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '0.375rem',
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
                style={{
                  background: '#91ccff',
                  color: 'white',
                  padding: '1rem',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.7 : 1
                }}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {responseMessage && (
                <div data-testid="contact-response-message" style={{
                  padding: '1rem',
                  borderRadius: '0.375rem',
                  background: responseMessage.includes('Thank you') ? '#d4edda' : '#f8d7da',
                  color: responseMessage.includes('Thank you') ? '#155724' : '#721c24',
                  border: `1px solid ${responseMessage.includes('Thank you') ? '#c3e6cb' : '#f5c6cb'}`
                }}>
                  {responseMessage}
                </div>
              )}
            </form>

            <div style={{ marginTop: '3rem', textAlign: 'center', color: '#666', fontFamily: "'Arial', sans-serif" }}>
              <p style={{ marginBottom: '0.5rem' }}>You can also reach us at:</p>
              <p style={{ fontWeight: '600', color: '#91ccff', fontSize: '1.1rem' }}>support@practice4sat.com</p>
            </div>
          </section>

          {/* Internal links for SEO */}
          <aside style={{ maxWidth: '800px', margin: '2rem auto 0', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem', fontFamily: "'Arial', sans-serif" }}>
              Explore Practice4SAT
            </h2>
            <nav aria-label="Related pages" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
              <Link to="/quick-quiz" data-testid="link-diagnostic-quiz" style={{ color: '#0030b3', textDecoration: 'underline', fontSize: '0.95rem', fontFamily: "'Arial', sans-serif" }}>
                Free SAT Diagnostic Quiz
              </Link>
              <a href="/#pricing" data-testid="link-study-plan" style={{ color: '#0030b3', textDecoration: 'underline', fontSize: '0.95rem', fontFamily: "'Arial', sans-serif" }}>
                Get Your Personalized Study Plan
              </a>
            </nav>
          </aside>
        </article>
      </main>

      <Footer />

      <style>{`
        @media (min-width: 768px) {
          .desktop-nav {
            display: flex !important;
          }
          .mobile-menu-btn {
            display: none !important;
          }
        }
        @media (max-width: 767px) {
          .desktop-nav {
            display: none !important;
          }
        }
        .footer-link:hover {
          color: #91ccff !important;
        }
      `}</style>
    </div>
  );
}
